import * as React from 'react';
import { Box, Divider, Paper, TextField, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useTaskflowBuilder } from '../taskflowBuilderContext';
import { MatterCategorySelector } from '../../matterSummary/selectors/MatterCategorySelector';
import { MatterTypeSelector } from '../../matterSummary/selectors/MatterTypeSelector';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(5, 3),
        },
        card: {
            width: '100%',
            maxWidth: 480,
            padding: theme.spacing(4),
            borderRadius: 10,
        },
        heading: {
            fontSize: 19,
            fontWeight: 700,
            textAlign: 'center',
        },
        sub: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            textAlign: 'center',
            marginTop: theme.spacing(0.5),
        },
        divider: {
            margin: theme.spacing(3, 0),
        },
        fieldRow: {
            marginBottom: theme.spacing(2.5),
        },
        label: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: theme.spacing(0.75),
        },
        help: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
        },
    })
);

export const NameScopeStep: React.FC = () => {
    const classes = useStyles();
    const { activeTaskflow, dispatch } = useTaskflowBuilder();

    if (!activeTaskflow) {
        return null;
    }

    const updateNameScope = (name: string, matterCategory: string[], matterType: string[]) => {
        dispatch({ type: 'UPDATE_NAME_SCOPE', name, matterCategory, matterType });
    };

    // tslint:disable-next-line: no-any
    const onMatterCategoryChange = (value?: any) => {
        const nextMatterCategory: string[] = (value ?? []).map((item: { label: string }) => item.label);
        updateNameScope(activeTaskflow.name, nextMatterCategory, activeTaskflow.matterType);
    };

    // tslint:disable-next-line: no-any
    const onMatterTypeChange = (value?: any) => {
        const nextMatterType: string[] = (value ?? []).map((item: { label: string }) => item.label);
        updateNameScope(activeTaskflow.name, activeTaskflow.matterCategory, nextMatterType);
    };

    return (
        <Box className={classes.root}>
            <Paper variant="outlined" className={classes.card}>
                <Typography className={classes.heading}>Name your Taskflow</Typography>
                <Typography className={classes.sub}>Step 1 of 2 — the header. You'll add tasks next.</Typography>
                <Divider className={classes.divider} />

                <Box className={classes.fieldRow}>
                    <Typography className={classes.label}>Taskflow name *</Typography>
                    <TextField
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        value={activeTaskflow.name}
                        onChange={event => updateNameScope(event.target.value, activeTaskflow.matterCategory, activeTaskflow.matterType)}
                    />
                    <Typography className={classes.help}>Free text — this is what fee-earners see when a matter picks it up.</Typography>
                </Box>

                <Box className={classes.fieldRow}>
                    <Typography className={classes.label}>Matter category (optional)</Typography>
                    <MatterCategorySelector
                        multiple={true}
                        size="small"
                        variant="outlined"
                        value={activeTaskflow.matterCategory.map(category => ({ label: category, value: category }))}
                        onSelection={onMatterCategoryChange}
                    />
                    <Typography className={classes.help}>Multi-select. Leave blank to make it available to every matter category.</Typography>
                </Box>

                <Box className={classes.fieldRow}>
                    <Typography className={classes.label}>Matter type (optional)</Typography>
                    <MatterTypeSelector
                        multiple={true}
                        size="small"
                        variant="outlined"
                        value={activeTaskflow.matterType.map(type => ({ label: type, value: type }))}
                        onSelection={onMatterTypeChange}
                    />
                    <Typography className={classes.help}>Multi-select. Narrows which matters this Taskflow can be attached to.</Typography>
                </Box>
            </Paper>
        </Box>
    );
};
